import { useMemo, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { DiffTable, WarningLine } from './backupShared';

// ─── Auswahl beim Wiederherstellen eines Baums aus einem Gesamt-Backup ───────
//
// Woher die Datei kommt – hochgeladen oder von der Storage Box geholt –, ändert
// an dieser Auswahl nichts. Deshalb steht sie hier einmal, und die beiden
// Varianten liefern nur die Daten und die Aktion.

export interface TreeInFile {
  id: string;
  name: string;
  description: string | null;
  counts: Record<string, number>;
}

export interface LocalTree {
  id: string;
  name: string;
  counts: Record<string, number>;
}

export interface Inspection {
  sourceVersion: number;
  exportedAt: string | null;
  trees: TreeInFile[];
  localTrees: LocalTree[];
}

/** Was der Aufrufer zum Einspielen braucht. */
export interface RestoreChoice {
  sourceTreeId: string;
  mode: 'new' | 'overwrite';
  targetTreeId?: string;
  confirmName?: string;
  newName?: string;
}

type Target = { mode: 'overwrite'; treeId: string } | { mode: 'new' };

export function TreeRestorePicker({
  info,
  busy,
  onRestore,
  onCancel,
}: {
  info: Inspection;
  busy: boolean;
  onRestore: (choice: RestoreChoice) => void;
  onCancel: () => void;
}) {
  const [sourceTreeId, setSourceTreeId] = useState(
    info.trees.length === 1 ? info.trees[0].id : '',
  );
  const [target, setTarget] = useState<Target>({ mode: 'new' });
  const [newName, setNewName] = useState('');
  const [confirmName, setConfirmName] = useState('');

  const chosen = useMemo(
    () => info.trees.find((t) => t.id === sourceTreeId) ?? null,
    [info, sourceTreeId],
  );
  const targetTree = useMemo(
    () =>
      target.mode === 'overwrite'
        ? (info.localTrees.find((t) => t.id === target.treeId) ?? null)
        : null,
    [info, target],
  );

  const canRestore =
    chosen !== null &&
    !busy &&
    // Beim Überschreiben muss der Name des Ziels stimmen. Der Platzhalter \0
    // kann nie eingetippt werden – ohne gewähltes Ziel bleibt es gesperrt.
    (target.mode === 'new' || confirmName.trim() === (targetTree?.name ?? '\0'));

  const submit = () => {
    if (!chosen) return;
    onRestore(
      target.mode === 'new'
        ? {
            sourceTreeId: chosen.id,
            mode: 'new',
            newName: newName.trim() || `${chosen.name} (aus Backup)`,
          }
        : {
            sourceTreeId: chosen.id,
            mode: 'overwrite',
            targetTreeId: target.treeId,
            confirmName,
          },
    );
  };

  if (info.trees.length === 0) {
    return (
      <WarningLine>
        In diesem Backup sind keine Stammbäume enthalten. Stammt es aus einer älteren
        Programmversion, kennt es noch keine Stammbäume – dafür ist das vollständige
        Wiederherstellen zuständig.
      </WarningLine>
    );
  }

  return (
    <div className="mt-4 space-y-3">
      <p className="text-xs text-slate-500">
        {info.exportedAt
          ? `Backup vom ${new Date(info.exportedAt).toLocaleString('de-DE')}`
          : 'Backup ohne Datumsangabe'}
        {info.sourceVersion < 2 && ' · altes Format'}
      </p>

      {info.sourceVersion < 2 && (
        <WarningLine>
          Dieses Backup stammt aus einer älteren Programmversion. Es enthält nur Personen,
          Beziehungen, Familien, Dateien und Quellen – Alben, Belege, Laufbahn, Lebensläufe und
          medizinische Angaben fehlen darin.
        </WarningLine>
      )}

      {/* Schritt 1: welcher Baum aus der Datei? */}
      <fieldset>
        <legend className="text-xs font-medium text-slate-700">
          Welcher Stammbaum aus dem Backup?
        </legend>
        <div className="mt-1.5 space-y-1.5">
          {info.trees.map((t) => (
            <label
              key={t.id}
              className={`flex cursor-pointer items-start gap-2 rounded-lg border p-2.5 text-xs ${
                sourceTreeId === t.id
                  ? 'border-blue-300 bg-blue-50/60'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <input
                type="radio"
                name="source-tree"
                checked={sourceTreeId === t.id}
                onChange={() => setSourceTreeId(t.id)}
                className="mt-0.5"
              />
              <span className="min-w-0 flex-1">
                <span className="font-medium text-slate-800">{t.name}</span>
                <span className="ml-1.5 text-slate-500">
                  {t.counts.persons} Personen, {t.counts.media} Dateien
                </span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Schritt 2: wohin? */}
      {chosen && (
        <fieldset>
          <legend className="text-xs font-medium text-slate-700">Wohin einspielen?</legend>
          <div className="mt-1.5 space-y-1.5">
            <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 p-2.5 text-xs hover:bg-slate-50">
              <input
                type="radio"
                name="target"
                checked={target.mode === 'new'}
                onChange={() => setTarget({ mode: 'new' })}
                className="mt-0.5"
              />
              <span className="min-w-0 flex-1">
                <span className="font-medium text-slate-800">Als neuen Stammbaum anlegen</span>
                <span className="block text-slate-500">
                  Überschreibt nichts. Zum Vergleichen, bevor man etwas ersetzt.
                </span>
              </span>
            </label>

            {info.localTrees.map((t) => (
              <label
                key={t.id}
                className={`flex cursor-pointer items-start gap-2 rounded-lg border p-2.5 text-xs ${
                  target.mode === 'overwrite' && target.treeId === t.id
                    ? 'border-red-300 bg-red-50/60'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="target"
                  checked={target.mode === 'overwrite' && target.treeId === t.id}
                  onChange={() => {
                    setTarget({ mode: 'overwrite', treeId: t.id });
                    setConfirmName('');
                  }}
                  className="mt-0.5"
                />
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-slate-800">„{t.name}" ersetzen</span>
                  <span className="block text-slate-500">
                    Der jetzige Inhalt ({t.counts.persons} Personen) wird ersetzt.
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {chosen && target.mode === 'new' && (
        <div>
          <label htmlFor="new-tree-name" className="block text-xs font-medium text-slate-700">
            Name des neuen Stammbaums
          </label>
          <input
            id="new-tree-name"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={`${chosen.name} (aus Backup)`}
            className="mt-1.5 w-full rounded border border-slate-300 px-2.5 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
      )}

      {chosen && targetTree && (
        <>
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <DiffTable current={targetTree.counts} incoming={chosen.counts} />
          </div>

          <div className="rounded-lg border border-red-200 bg-red-50/60 p-3">
            <label htmlFor="global-confirm" className="block text-xs font-medium text-red-800">
              Zur Bestätigung den Namen des Ziel-Stammbaums eingeben:{' '}
              <span className="font-mono">{targetTree.name}</span>
            </label>
            <input
              id="global-confirm"
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={targetTree.name}
              autoComplete="off"
              className="mt-1.5 w-full rounded border border-red-300 bg-white px-2.5 py-2 text-sm focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
            />
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs leading-relaxed text-emerald-800">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            Der jetzige Stand dieses Stammbaums wird vorher als Datei heruntergeladen.
          </div>
        </>
      )}

      {chosen && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={submit}
            disabled={!canRestore}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-40 ${
              target.mode === 'new'
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {busy
              ? 'Wird eingespielt…'
              : target.mode === 'new'
                ? 'Als neuen Stammbaum anlegen'
                : 'Überschreiben'}
          </button>
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            Abbrechen
          </button>
        </div>
      )}
    </div>
  );
}
